import { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import api from './api';
import useIsMobile from './hooks/useIsMobile';

import Login from './components/Login';
import SprintBoard from './components/SprintBoard';
import ProjectDashboard from './components/ProjectDashboard';
import ProjectDrawer from './components/ProjectDrawer';
import Toast from './components/Toast';

import { LoginMobile, SprintBoardMobile, ProjectDashboardMobile } from './components/mobile';

function App() {
  const isMobile = useIsMobile();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [projects, setProjects] = useState([]);

  const [currentProject, setCurrentProject] = useState(() => {
    try {
      const saved = sessionStorage.getItem('currentProject');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const heartbeatCleanup = useRef(null);

  useEffect(() => {
    if (currentProject) {
      sessionStorage.setItem('currentProject', JSON.stringify(currentProject));
    } else {
      sessionStorage.removeItem('currentProject');
    }
  }, [currentProject]);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type, id: Date.now() });
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      const usersList = await api.getUsers();
      setUsers(usersList);
    } catch (err) {
      // Silently fail - users list is non-critical
    }
  }, []);

  const loadProjects = useCallback(async (uid, isAdmin) => {
    try {
      const list = isAdmin ? await api.getAllProjects() : await api.getMyProjects(uid);
      setProjects(list);
    } catch {
      // Silently fail - drawer is non-critical
    }
  }, []);

  const isAdmin = userProfile?.role === 'admin';

  const handleCreateProject = useCallback(async ({ name, description, type, members }) => {
    const project = await api.createProject({ name, description, type, createdBy: user.uid });
    for (const uid of members) {
      if (uid !== user.uid) await api.addProjectMember(project.id, uid);
    }
    await loadProjects(user.uid, isAdmin);
    showToast('Project created', 'success');
    return project;
  }, [user, isAdmin, loadProjects, showToast]);

  const handleAddMembers = useCallback(async (projectId, uids) => {
    for (const uid of uids) {
      await api.addProjectMember(projectId, uid);
    }
    await loadProjects(user.uid, isAdmin);
    showToast(`${uids.length} member${uids.length !== 1 ? 's' : ''} added`, 'success');
  }, [user, isAdmin, loadProjects, showToast]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const profile = await api.getUserProfile(user.uid);
      if (profile) setUserProfile(profile);
    } catch (err) {
      // Silently fail
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = api.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          let profile = await api.getUserProfile(firebaseUser.uid);

          if (!profile) {
            // Check if users collection is empty (first-user bootstrap)
            const allUsers = await api.getUsers();
            if (allUsers.length === 0) {
              // First user ever - auto-create as admin
              profile = await api.createUserProfile(firebaseUser.uid, {
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                role: 'admin',
              });
            } else {
              // User exists in Auth but not in Firestore users collection
              setUnauthorized(true);
              setLoading(false);
              return;
            }
          }

          setUserProfile(profile);
          setUnauthorized(false);
          await refreshUsers();
          await loadProjects(firebaseUser.uid, profile?.role === 'admin');

          // Start presence heartbeat
          if (heartbeatCleanup.current) heartbeatCleanup.current();
          heartbeatCleanup.current = api.startPresenceHeartbeat(firebaseUser.uid);
        } catch (err) {
          showToast('Failed to load user profile', 'error');
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setUsers([]);
        setProjects([]);
        setUnauthorized(false);
        setCurrentProject(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (heartbeatCleanup.current) heartbeatCleanup.current();
    };
  }, [refreshUsers, loadProjects, showToast]);

  const handleLogin = (userData) => {
    setUser(userData);
    showToast('Signed in successfully', 'success');
  };

  const handleLogout = async () => {
    try {
      if (heartbeatCleanup.current) {
        heartbeatCleanup.current();
        heartbeatCleanup.current = null;
      }
      await api.signOut();
      setUser(null);
      setUserProfile(null);
      setUsers([]);
      setCurrentProject(null);
      showToast('Signed out', 'info');
    } catch (err) {
      setUser(null);
      setUserProfile(null);
      setCurrentProject(null);
      showToast(err.message || 'Sign out failed', 'error');
    }
  };

  const handleSelectProject = (project) => {
    setCurrentProject(project);
  };

  const handleBackToProjects = () => {
    setCurrentProject(null);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="app">
        <div className="unauthorized-page">
          <h2>Access Denied</h2>
          <p>Your account has not been authorized by an administrator. Please contact your admin to get access.</p>
          <button className="btn btn-ghost" onClick={handleLogout}>Sign Out</button>
        </div>
        {toast && (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  const renderView = () => {
    if (!user) {
      const LoginComponent = isMobile ? LoginMobile : Login;
      return <LoginComponent onLogin={handleLogin} showToast={showToast} />;
    }

    if (!currentProject) {
      const DashboardComponent = isMobile ? ProjectDashboardMobile : ProjectDashboard;
      return (
        <DashboardComponent
          user={user}
          userProfile={userProfile}
          users={users}
          onSelectProject={handleSelectProject}
          onLogout={handleLogout}
          showToast={showToast}
          refreshUsers={refreshUsers}
        />
      );
    }

    const BoardComponent = isMobile ? SprintBoardMobile : SprintBoard;
    const board = (
      <BoardComponent
        user={user}
        userProfile={userProfile}
        users={users}
        onLogout={handleLogout}
        showToast={showToast}
        refreshUsers={refreshUsers}
        refreshProfile={refreshProfile}
        project={currentProject}
        onBackToProjects={handleBackToProjects}
        onProjectUpdated={setCurrentProject}
        onAddMembers={handleAddMembers}
      />
    );

    if (isMobile) return board;

    return (
      <div className="app-project-layout">
        <ProjectDrawer
          projects={projects}
          onSelectProject={handleSelectProject}
          isAdmin={isAdmin}
          users={users}
          onCreateProject={handleCreateProject}
          onAddMembers={handleAddMembers}
          showToast={showToast}
        />
        <div className="app-project-main">{board}</div>
      </div>
    );
  };

  return (
    <div className="app">
      {renderView()}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default App;
